import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { MENU_ITEMS } from '../constants/menu-items';
import { MenuItemType } from '../models/menu-item.type';

@Injectable({
  providedIn: 'root'
})
export class NavigationService {
  private pageTitle = new BehaviorSubject<string>('Dashboard');
  public pageTitle$ = this.pageTitle.asObservable();

  private breadcrumbs = new BehaviorSubject<string[]>(['Home']);
  public breadcrumbs$ = this.breadcrumbs.asObservable();

  constructor(private router: Router) {
    this.initializeNavigation();
  }

  private initializeNavigation(): void {
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.updatePageTitle(event.url);
      });
  }

  private updatePageTitle(url: string): void {
    const { title, breadcrumbs } = this.getPageInfoFromUrl(url);
    this.pageTitle.next(title);
    this.breadcrumbs.next(breadcrumbs);
  }

  private getPageInfoFromUrl(url: string): { title: string; breadcrumbs: string[] } {
    // Remove query parameters and fragments
    const cleanUrl = url.split('?')[0].split('#')[0];
    
    // Find the matching menu item
    const menuItem = this.findMenuItemByRoute(cleanUrl, MENU_ITEMS);
    
    if (menuItem) {
      const breadcrumbs = this.buildBreadcrumbs(menuItem, cleanUrl);
      return {
        title: menuItem.label,
        breadcrumbs: ['Home', ...breadcrumbs]
      };
    }

    // Handle special cases
    return this.getSpecialPageInfo(cleanUrl);
  }

  private findMenuItemByRoute(route: string, menuItems: MenuItemType[]): MenuItemType | null {
    for (const item of menuItems) {
      // Check exact match
      if (item.route === route) {
        return item;
      }
      
      // Check if route starts with menu item route (for dynamic routes)
      if (route.startsWith(item.route) && item.route !== '#') {
        return item;
      }
      
      // Search in children
      if (item.children) {
        const childItem = this.findMenuItemByRoute(route, item.children);
        if (childItem) {
          return childItem;
        }
      }
    }
    return null;
  }

  private buildBreadcrumbs(menuItem: MenuItemType, currentRoute: string): string[] {
    const breadcrumbs: string[] = [];
    
    // Find parent menu item
    const parentItem = this.findParentMenuItem(menuItem, MENU_ITEMS);
    if (parentItem) {
      breadcrumbs.push(parentItem.label);
    }
    
    breadcrumbs.push(menuItem.label);
    return breadcrumbs;
  }

  private findParentMenuItem(targetItem: MenuItemType, menuItems: MenuItemType[]): MenuItemType | null {
    for (const item of menuItems) {
      if (item.children) {
        const found = item.children.find(child => child.id === targetItem.id);
        if (found) {
          return item;
        }
      }
    }
    return null;
  }

  private getSpecialPageInfo(url: string): { title: string; breadcrumbs: string[] } {
    // Handle special routes that might not be in menu
    const specialRoutes: { [key: string]: { title: string; breadcrumbs: string[] } } = {
      '/dashboard': { title: 'Dashboard', breadcrumbs: ['Home', 'Dashboard'] },
      '/profile': { title: 'My Profile', breadcrumbs: ['Home', 'My Profile'] },
      '/profile/kyc': { title: 'KYC Verification', breadcrumbs: ['Home', 'Profile', 'KYC'] },
      '/profile/image-upload': { title: 'Upload Profile Image', breadcrumbs: ['Home', 'Profile', 'Upload Image'] },
      '/change-password': { title: 'Change Password', breadcrumbs: ['Home', 'Profile', 'Change Password'] },
      '/user-tree-view': { title: 'Tree View', breadcrumbs: ['Home', 'Tree View'] },
      '/hierarchy': { title: 'Hierarchy', breadcrumbs: ['Home', 'Hierarchy'] },
      '/admin/user-management': { title: 'User Management', breadcrumbs: ['Home', 'Admin', 'User Management'] },
      '/admin/points-rules-config': { title: 'Points Rules Config', breadcrumbs: ['Home', 'Admin', 'Points Rules Config'] },
      '/admin/manual-points-adjust': { title: 'Manual Points Adjust', breadcrumbs: ['Home', 'Admin', 'Manual Points Adjust'] },

      

      
      // Products routes
      '/products': { title: 'All Products', breadcrumbs: ['Home', 'Products', 'All Products'] },
      '/products/cart': { title: 'Shopping Cart', breadcrumbs: ['Home', 'Products', 'Shopping Cart'] },
      '/products/purchased': { title: 'Purchased Products', breadcrumbs: ['Home', 'Products', 'Purchased Products'] },
      '/products/wishlist': { title: 'Wishlist', breadcrumbs: ['Home', 'Products', 'Wishlist'] },
      '/product-management': { title: 'Add Product', breadcrumbs: ['Home', 'Products', 'Add Product'] },
      

      
      // Master Data routes
      '/categories': { title: 'Categories', breadcrumbs: ['Home', 'Master', 'Categories'] },
      '/master/product-subcategories': { title: 'Product SubCategories', breadcrumbs: ['Home', 'Master', 'Product SubCategories'] },
      '/master/product-features': { title: 'Product Features', breadcrumbs: ['Home', 'Master', 'Product Features'] },
      '/statuses': { title: 'Status Master', breadcrumbs: ['Home', 'Master', 'Status Master'] },
      '/profile/relationships': { title: 'Relationship Master', breadcrumbs: ['Home', 'Master', 'Relationship Master'] },
    };

    // Check for exact match
    if (specialRoutes[url]) {
      return specialRoutes[url];
    }

    // Check for partial matches (for dynamic routes)
    for (const route in specialRoutes) {
      if (url.startsWith(route)) {
        return specialRoutes[route];
      }
    }

    // Default fallback - try to generate a title from the URL
    const urlSegments = url.split('/').filter(segment => segment);
    if (urlSegments.length > 0) {
      const lastSegment = urlSegments[urlSegments.length - 1];
      const title = this.formatTitle(lastSegment);
      return { 
        title, 
        breadcrumbs: ['Home', ...urlSegments.slice(0, -1).map(segment => this.formatTitle(segment)), title] 
      };
    }

    return { title: 'Dashboard', breadcrumbs: ['Home', 'Dashboard'] };
  }
  
  private formatTitle(segment: string): string {
    return segment
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  // Method to manually set page title (useful for complex scenarios)
  setPageTitle(title: string, breadcrumbs?: string[]): void {
    this.pageTitle.next(title);
    if (breadcrumbs) {
      this.breadcrumbs.next(breadcrumbs);
    }
  }

  // Get current page title
  getCurrentPageTitle(): string {
    return this.pageTitle.value;
  }

  // Get current breadcrumbs
  getCurrentBreadcrumbs(): string[] {
    return this.breadcrumbs.value;
  }
}
